import { cn } from '@quadralize/utils'
import type { VariantProps } from 'class-variance-authority'
import { type HTMLMotionProps, motion } from 'framer-motion'
import * as React from 'react'
import { cardVariants } from './card.variants'

interface CardProps extends HTMLMotionProps<'div'>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, shadow, hoverable, ...props }, ref) => {
    // Separate motion props from standard props to avoid type clashes
    const motionProps = hoverable
      ? {
          whileHover: { y: -4, scale: 1.01 },
          whileTap: { scale: 0.99 },
        }
      : {}

    return (
      <motion.div
        ref={ref}
        className={cn(cardVariants({ variant, padding, shadow, hoverable, className }))}
        {...motionProps}
        {...props}
      />
    )
  },
)

Card.displayName = 'Card'

interface CardHeaderProps extends React.ComponentProps<'div'> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('flex flex-col gap-1.5', className)} {...props} />
  },
)

CardHeader.displayName = 'CardHeader'

interface CardTitleProps extends React.ComponentProps<'h3'> {}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn(
          'text-lg font-semibold leading-none tracking-tight text-text-primary',
          className,
        )}
        {...props}
      />
    )
  },
)

CardTitle.displayName = 'CardTitle'

interface CardDescriptionProps extends React.ComponentProps<'p'> {}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => {
    return <p ref={ref} className={cn('text-sm text-text-secondary', className)} {...props} />
  },
)

CardDescription.displayName = 'CardDescription'

interface CardContentProps extends React.ComponentProps<'div'> {}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('pt-0', className)} {...props} />
  },
)

CardContent.displayName = 'CardContent'

interface CardFooterProps extends React.ComponentProps<'div'> {}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('flex items-center pt-0', className)} {...props} />
  },
)

CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, cardVariants }
